import { useEffect, useRef } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSpring,
  withTiming,
  Easing,
} from 'react-native-reanimated';
import { theme } from '@/constants/theme';

type Block = { icon?: string; title: string; body: string; accent?: string };

function BentoCard({
  block,
  index,
  delay,
}: {
  block: Block;
  index: number;
  delay: number;
}) {
  const mountY = useSharedValue(20);
  const mountOpacity = useSharedValue(0);
  const hoverScale = useSharedValue(1);
  const hoverElevation = useSharedValue(0);
  const hoverBorder = useSharedValue(0.3);

  const accent = block.accent ?? '#3B82F6';
  const hex = accent.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16) || 59;
  const g = parseInt(hex.slice(2, 4), 16) || 130;
  const b = parseInt(hex.slice(4, 6), 16) || 246;

  useEffect(() => {
    mountOpacity.value = withDelay(delay, withTiming(1, { duration: 400, easing: Easing.out(Easing.cubic) }));
    mountY.value = withDelay(delay, withSpring(0, { damping: 16, stiffness: 120 }));
  }, [delay]);

  const cardStyle = useAnimatedStyle(() => ({
    opacity: mountOpacity.value,
    transform: [
      { translateY: mountY.value },
      { scale: hoverScale.value },
    ],
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: `0 ${4 + hoverElevation.value * 12}px ${12 + hoverElevation.value * 20}px rgba(0,0,0,${0.3 + hoverElevation.value * 0.2}), 0 0 ${hoverElevation.value * 18}px rgba(${r},${g},${b},${hoverElevation.value * 0.3})`,
          borderColor: `rgba(${r},${g},${b},${hoverBorder.value})`,
        } as any)
      : {}),
  }));

  return (
    <Pressable
      style={{ flexBasis: '48%' as any }}
      onHoverIn={() => {
        hoverScale.value = withSpring(1.02, { damping: 14, stiffness: 200 });
        hoverElevation.value = withTiming(1, { duration: 200 });
        hoverBorder.value = withTiming(0.8, { duration: 200 });
      }}
      onHoverOut={() => {
        hoverScale.value = withSpring(1, { damping: 14, stiffness: 200 });
        hoverElevation.value = withTiming(0, { duration: 300 });
        hoverBorder.value = withTiming(0.3, { duration: 300 });
      }}
      onPressIn={() => {
        hoverScale.value = withSpring(0.97, { damping: 16, stiffness: 280 });
      }}
      onPressOut={() => {
        hoverScale.value = withSpring(1, { damping: 14, stiffness: 200 });
      }}
    >
      <Animated.View style={[s.card, cardStyle]}>
        {!!block.icon && <Text style={s.icon}>{block.icon}</Text>}
        <Text style={s.title}>{block.title}</Text>
        <Text style={s.body}>{block.body}</Text>
      </Animated.View>
    </Pressable>
  );
}

export default function MagicBento({ blocks }: { blocks: Block[] }) {
  return (
    <View style={s.grid}>
      {blocks.map((b, i) => (
        <BentoCard key={i} block={b} index={i} delay={i * 80} />
      ))}
    </View>
  );
}

const s = StyleSheet.create({
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  card: {
    flexBasis: '48%' as any,
    backgroundColor: '#13172A',
    borderWidth: 1,
    borderColor: '#2A315A',
    borderRadius: 16,
    padding: 14,
    minHeight: 120,
  },
  icon: { fontSize: 20, marginBottom: 8 },
  title: { color: '#fff', fontFamily: theme.fonts.bold, textAlign: 'right', fontSize: 16 },
  body: { color: '#A8AFD0', fontFamily: theme.fonts.regular, textAlign: 'right', marginTop: 6, fontSize: 13 },
});
